import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { INSIGHTS_BACKEND_URL, NEST_BACKEND_URL } from "@/lib/axios";
import { QueryConfig } from "@/lib/react-query";
import { getMappedChannelNames } from "@/shared/helpers/getMappedChannelNames";
import { useModuleEndpoint } from "@/shared/hooks/useModuleEndpoint";
import { ChannelDataWithSourceAndTotal } from "@/shared/types";
import { axiosApi } from "@/Utils";

const getPublisherROIsV2 = ({
  brand,
  category,
  product,
  channels,
  startDate,
  endDate,
  geographies = [],
  graphView,
  tableView,
  geoViewToggle,
  businessUnitToggle = "aggregate",
  publishers = [],
}: {
  brand: string;
  category: string;
  product: string;
  channels: string[];
  graphView: "channel" | "insight";
  tableView: "channel" | "insight";
  geoViewToggle: "aggregate" | "split";
  businessUnitToggle?: "aggregate" | "split";
  startDate?: string | undefined;
  endDate?: string | undefined;
  geographies?: string[] | undefined;
  publishers?: string[] | undefined;
}): Promise<{
  chartView: ChannelDataWithSourceAndTotal[];
  tableView: ChannelDataWithSourceAndTotal[];
  totals: {
    totalSpend: number;
    totalRevenue: number;
    totalRoi: number;
  };
}> => {
  const payload: Record<string, any> = {
    brand,
    category,
    product,
    channels,
    startDate,
    endDate,
    geographies,
    graphView,
    tableView,
    geoViewToggle,
    businessUnitToggle,
    publishers,
  };

  return axiosApi.get("/api/v2/publishers", { params: payload, baseURL: INSIGHTS_BACKEND_URL });
};

const _getPublisherROIsNest = ({
  brand,
  category,
  product,
  channels,
  startDate,
  endDate,
  geographies = [],
  graphView,
  tableView,
  geoViewToggle,
  businessUnitToggle = "aggregate",
  publishers = [],
}: {
  brand: string;
  category: string;
  product: string;
  channels: string[];
  graphView: "channel" | "insight";
  tableView: "channel" | "insight";
  geoViewToggle: "aggregate" | "split";
  businessUnitToggle?: "aggregate" | "split";
  startDate?: string | undefined;
  endDate?: string | undefined;
  geographies?: string[] | undefined;
  publishers?: string[] | undefined;
}): Promise<{
  chartView: ChannelDataWithSourceAndTotal[];
  tableView: ChannelDataWithSourceAndTotal[];
  totals: {
    totalSpend: number;
    totalRevenue: number;
    totalRoi: number;
  };
}> => {
  const params = new URLSearchParams();

  if (brand) params.append("brand", brand);
  if (category) params.append("category", category);
  if (product) params.append("product", product);
  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);
  if (graphView) params.append("graphView", graphView);
  if (tableView) params.append("tableView", tableView);
  if (geoViewToggle) params.append("geoViewToggle", geoViewToggle);
  if (businessUnitToggle) params.append("businessUnitToggle", businessUnitToggle);

  if (channels && channels.length > 0) {
    channels.forEach((channel) => {
      params.append("channels", channel);
    });
  }

  if (geographies && geographies.length > 0) {
    geographies.forEach((geography) => {
      params.append("geographies", geography);
    });
  }

  if (publishers && publishers.length > 0) {
    publishers.forEach((publisher) => {
      params.append("publishers", publisher);
    });
  }

  return axiosApi.get(`/api/publishers?${params.toString()}`, {
    baseURL: NEST_BACKEND_URL,
  });
};

export const usePublisherROIsV3 = ({
  brand,
  category,
  product,
  channels,
  startDate,
  endDate,
  geographies = [],
  graphView,
  tableView,
  geoViewToggle,
  businessUnitToggle = "split",
  config,
  publishers = [],
}: {
  brand: string;
  category: string;
  product: string;
  channels: string[];
  startDate: string;
  endDate: string;
  graphView: "channel" | "insight";
  tableView: "channel" | "insight";
  geoViewToggle: "aggregate" | "split";
  businessUnitToggle: "aggregate" | "split";
  geographies?: string[];
  config?: QueryConfig<typeof getPublisherROIsV2 | typeof _getPublisherROIsNest>;
  publishers?: string[];
}) => {
  const endpointType = useModuleEndpoint("publishers");

  const queryKey = useMemo(
    () => [
      "publishers-roi-v3",
      brand,
      startDate,
      endDate,
      geographies,
      graphView,
      tableView,
      geoViewToggle,
      businessUnitToggle,
      publishers,
      category,
      product,
      channels,
      endpointType,
    ],
    [
      brand,
      startDate,
      endDate,
      geographies,
      graphView,
      tableView,
      geoViewToggle,
      businessUnitToggle,
      publishers,
      category,
      product,
      channels,
      endpointType,
    ]
  );

  const queryFn = useCallback(() => {
    if (endpointType === "new") {
      return _getPublisherROIsNest({
        brand,
        category,
        product,
        channels,
        startDate,
        endDate,
        geographies,
        graphView,
        tableView,
        geoViewToggle,
        businessUnitToggle,
        publishers,
      });
    } else {
      return getPublisherROIsV2({
        brand,
        category,
        product,
        channels,
        startDate,
        endDate,
        geographies,
        graphView,
        tableView,
        geoViewToggle,
        businessUnitToggle,
        publishers,
      });
    }
  }, [
    brand,
    category,
    product,
    channels,
    startDate,
    endDate,
    geographies,
    graphView,
    tableView,
    geoViewToggle,
    businessUnitToggle,
    publishers,
    endpointType,
  ]);

  return useQuery({
    ...config,
    queryKey,
    queryFn,
    select: (data) => ({
      ...data,
      tableView: getMappedChannelNames(data.tableView),
      chartView: getMappedChannelNames(data.chartView),
    }),
  });
};
